import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { ApiCreateDocumentGenerationJob, ApiService, CompanyService, DocumentReferencesView } from '../../../../../api';
import { useStore } from '../../../../../common/store';
import { UseStoreValues } from '../../../../../common/types/store';
import { buildEffortPayload, EffortSelector } from '../../../../../components/EffortSelector';
import type { EffortSelection } from '../../../../../components/EffortSelector';
import { useEventBus } from '../../../../../context';
import { useAsyncProcessor } from '../../../../../context/AsyncProcessorContext/AsyncProcessorContext';
import { createCapTableGenerationOnResult } from '../../../../../services/capTableGenerationResult';
import { useDocuments } from '../../../../../services/hooks';
import { extractResponseErrorMessage, Logger } from '../../../../../utilities';
import { AIGradientButton } from '../../../../DocumentGenerationJobs/components/AIGradient';
import {
  CAP_TABLE_GENERATION_JOB_TITLE,
  useActiveCapTableGeneration,
} from '../generation-in-progress/useActiveCapTableGeneration';

interface SelectedFile {
  file_id: number;
  label: string;
}

interface GenerateCapTableJobProps {
  selectedMeasurementDate?: { id?: number };
  // The cap table's reference scope (reference_object_type + feature id) so the picker can offer
  // only documents that are referenced on the cap table, matching the drawer's references list.
  currentPage?: string;
  referencedFeatureId?: number | string;
  // Takes over the header row's action slot from the built-in button. The owner of this node opens
  // the picker through dialogOpen instead.
  trigger?: ReactNode;
  dialogOpen?: boolean;
  onDialogOpenChange?: (open: boolean) => void;
}

const GenerateCapTableJob = ({
  selectedMeasurementDate,
  currentPage,
  referencedFeatureId,
  trigger,
  dialogOpen: dialogOpenProp,
  onDialogOpenChange,
}: GenerateCapTableJobProps) => {
  const [storeValue] = useStore() as unknown as UseStoreValues;
  const { companyInfo, firmInfo } = storeValue;
  const { addJob, removeJob } = useAsyncProcessor();
  // Publish-only: the completion event is consumed by the cap-table page root
  // (useCapTableGenerationRefresh), which re-fetches the grid.
  const { publish } = useEventBus();
  const { isGenerating: hasActiveGeneration, documentCount: activeDocumentCount } = useActiveCapTableGeneration();

  const [uncontrolledDialogOpen, setUncontrolledDialogOpen] = useState(false);
  const isDialogControlled = dialogOpenProp !== undefined;
  const dialogOpen = isDialogControlled ? dialogOpenProp : uncontrolledDialogOpen;
  const setDialogOpen = (open: boolean) => {
    if (isDialogControlled) {
      onDialogOpenChange?.(open);
      return;
    }
    setUncontrolledDialogOpen(open);
  };
  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);
  const [effortSelection, setEffortSelection] = useState<EffortSelection | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  // Anchor used only to resolve which window the picker currently lives in. When the drawer is
  // popped out to a separate window, its Emotion styles live in that window's head, so the MUI
  // Dialog/Autocomplete popper must portal into that window's body - not the host document.body
  // (MUI's default), which would render the menu unstyled in the wrong window.
  const anchorRef = useRef<HTMLDivElement>(null);
  const getPortalContainer = useCallback(() => anchorRef.current?.ownerDocument?.body ?? document.body, []);

  const { getReferencesByMeasurementDateId, foundReferencedDocuments } = useDocuments() as unknown as {
    getReferencesByMeasurementDateId: (mdId: number) => Promise<DocumentReferencesView[] | unknown>;
    foundReferencedDocuments: (
      docs: DocumentReferencesView[] | null,
      currentPage: string,
      referencedFeatureId: number | string
    ) => DocumentReferencesView[];
  };

  const [referencedDocuments, setReferencedDocuments] = useState<DocumentReferencesView[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Only the files referenced on this cap table for the current measurement date, deduped by file
  // id (one file can back multiple document references, but the job payload sends file ids). Gate
  // on isLoading so no stale file is ever listed or selectable while a fresh fetch is inflight.
  const documentFileOptions = useMemo(() => {
    if (isLoading) return [];
    const seen = new Set<number>();
    return referencedDocuments.filter(doc => {
      const id = doc.file?.id;
      if (id == null || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, [referencedDocuments, isLoading]);

  const handleOpen = useCallback(async () => {
    setSelectedFiles([]);
    setReferencedDocuments([]);
    setEffortSelection(null);
    setDialogOpen(true);
    // Fetch on open (not on mount) so documents referenced via the adjacent panel after page load
    // are picked up, scoped to the cap table and the measurement date the panel renders.
    if (selectedMeasurementDate?.id == null || currentPage == null || referencedFeatureId == null) {
      return;
    }
    setIsLoading(true);
    try {
      const refs = await getReferencesByMeasurementDateId(selectedMeasurementDate.id);
      setReferencedDocuments(
        Array.isArray(refs) ? foundReferencedDocuments(refs, currentPage, referencedFeatureId) : []
      );
    } finally {
      setIsLoading(false);
    }
    // getReferencesByMeasurementDateId / foundReferencedDocuments read company/state from the hook
    // closure, so adding them would only rebind this callback on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMeasurementDate?.id, currentPage, referencedFeatureId]);

  // An outside trigger opens the picker by flipping dialogOpen, so the document fetch that the
  // built-in button does on click has to run here instead.
  const hasOutsideTrigger = Boolean(trigger);
  const wasDialogOpen = useRef(false);
  useEffect(() => {
    if (dialogOpen && !wasDialogOpen.current && hasOutsideTrigger) {
      handleOpen();
    }
    wasDialogOpen.current = Boolean(dialogOpen);
  }, [dialogOpen, hasOutsideTrigger, handleOpen]);

  const handleConfirm = async () => {
    if (selectedFiles.length === 0 || !companyInfo?.id) return;
    setIsGenerating(true);
    setDialogOpen(false);

    const companyIdStr = companyInfo.id.toString();

    try {
      const newJob = await ApiService.apiCompanyDocumentGenerationJobsNewCreate(companyIdStr, {
        processing_type: ApiCreateDocumentGenerationJob.processing_type.CAP_TABLE,
        reference_files: selectedFiles.map(f => f.file_id.toString()) as any,
        desired_termination_state: 'COMPLETE',
        // Stamp the picker's measurement date (same as the Reprocess path in
        // CapTableWidgetDrawer) so applying the result targets THIS cap table
        // instead of falling back to the company's most recent measurement date.
        metadata: {
          measurement_date_id: selectedMeasurementDate?.id,
        },
        parser_overrides: [
          {
            file_type: 'xlsx',
            xlsx_parsing_mode: 'JSON_CONVERSION',
            minify_json: true,
          } as any,
        ],
        ...buildEffortPayload(effortSelection),
      } as any);

      addJob({
        id: newJob.job_id,
        companyId: companyIdStr,
        details: {
          title: CAP_TABLE_GENERATION_JOB_TITLE,
          description: `Processing ${selectedFiles.length} document${selectedFiles.length > 1 ? 's' : ''}`,
        },
        pollingConfig: {
          pollingFunction: async () => CompanyService.apiCompanyDocumentGenerationJobsRead(companyIdStr, newJob.job_id),
          pollingInterval: 3000,
          pollingArgs: [],
        },
        onResult: createCapTableGenerationOnResult({
          companyId: companyIdStr,
          logContext: 'GenerateCapTableJob',
          publish,
          removeJob,
        }),
        onError: (error: Error) => {
          Logger.error('GenerateCapTableJob - Generate job polling error', error);
        },
      });

      enqueueSnackbar('Cap table processing started. The cap table will update when the job completes.', {
        variant: 'info',
      });
    } catch (error: any) {
      Logger.error('GenerateCapTableJob - Error creating cap table generation job', error);
      const errorMessage = extractResponseErrorMessage(error) ?? 'Failed to start cap table processing';
      enqueueSnackbar(`Error: ${errorMessage}`, { variant: 'error' });
    } finally {
      setIsGenerating(false);
      setSelectedFiles([]);
    }
  };

  return (
    <>
      <Box
        ref={anchorRef}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, px: 1, pt: 1 }}>
        {hasActiveGeneration && (
          <Chip
            size="small"
            color="info"
            variant="outlined"
            data-testid="generate-cap-table-in-progress"
            icon={<CircularProgress size={14} color="inherit" />}
            label={
              activeDocumentCount
                ? `Extracting cap table from ${activeDocumentCount} document${activeDocumentCount > 1 ? 's' : ''}...`
                : 'Extracting cap table...'
            }
          />
        )}
        {trigger ?? (
          <AIGradientButton onClick={handleOpen} size="small" disabled={hasActiveGeneration}>
            Generate Cap Table
          </AIGradientButton>
        )}
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        container={getPortalContainer}
        // Let the inline dropdown float out below the field instead of being clipped by the dialog
        // card's default overflow:auto (paired with disablePortal + overflow:visible on the content).
        slotProps={{ paper: { sx: { overflow: 'visible' } } }}>
        <DialogTitle>Generate Cap Table from Documents</DialogTitle>
        <DialogContent sx={{ overflow: 'visible' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select from the documents referenced on this cap table. When the job completes, AI-extracted securities will
            be applied to the cap table.
          </Typography>
          <Autocomplete
            multiple
            options={documentFileOptions}
            disableCloseOnSelect
            value={documentFileOptions.filter(opt => selectedFiles.some(s => s.file_id === opt.file?.id))}
            onChange={(_, selected) =>
              setSelectedFiles(
                selected
                  .filter(opt => opt.file?.id != null)
                  .map(opt => ({
                    file_id: opt.file!.id!,
                    label: opt.file?.filename ?? 'Unknown',
                  }))
              )
            }
            getOptionLabel={option => option?.file?.filename ?? 'Unknown file'}
            getOptionKey={option => option.document_reference_id}
            renderOption={(props, option, { selected }) => {
              const { key, ...otherProps } = props;
              return (
                <li key={key} {...otherProps} style={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox checked={selected} style={{ marginRight: 8 }} />
                  {option?.file ? `${option.file.filename}.${option.file.file_type}` : 'Unknown file'}
                </li>
              );
            }}
            renderInput={params => <TextField {...params} variant="outlined" placeholder="Select documents" />}
            isOptionEqualToValue={(option, value) => option.document_reference_id === value.document_reference_id}
            // Inline (no portal) so the listbox shares the field's exact coordinate/zoom context and
            // stays aligned in the popped-out window; the dialog's overflow:visible lets it float out
            // freely, and the listbox keeps its own max-height + scrollbar for long lists.
            disablePortal
          />
          {selectedMeasurementDate?.id == null ? (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Select a measurement date to load documents
            </Typography>
          ) : (
            // Only after the fetch settles can we tell an empty cap table apart from one still
            // loading, so hold the empty-state until isLoading clears to avoid a flash.
            !isLoading &&
            documentFileOptions.length === 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                No documents are referenced on this cap table for this measurement date
              </Typography>
            )
          )}
          <Box sx={{ mt: 3 }}>
            <EffortSelector firmId={firmInfo?.id ?? null} value={effortSelection} onChange={setEffortSelection} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <AIGradientButton onClick={handleConfirm} disabled={isGenerating || selectedFiles.length === 0}>
            Start Job
          </AIGradientButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GenerateCapTableJob;
